/**
 * Required measurement sizing.
 *
 * The sizing UI is rendered outside Shopify's product <form> and associates
 * its inputs through the HTML `form` attribute. This component therefore owns
 * validation and keeps the main, sticky and accelerated purchase controls
 * locked until the customer has made an explicit valid selection.
 */

if (!customElements.get('product-measurement-sizing')) {
  class ProductMeasurementSizing extends HTMLElement {
    constructor() {
      super();
      this.currentUnit = 'inch';
      this.currentMode = 'standard';
      this.form = null;
      this.proxyForms = [];
      this.proxyObservers = [];
      this.formObserver = null;
      this.variantAvailable = true;
      this.lastFocusedElement = null;
      this.boundFormSubmit = this.onFormSubmit.bind(this);
      this.boundFormClick = this.onFormClick.bind(this);
      this.boundVariantChange = this.onVariantChange.bind(this);
      this.boundKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.initElements();
      this.bindEvents();
      this.updateDisplay();
      this.connectForm();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundFormSubmit, true);
      this.form?.removeEventListener('click', this.boundFormClick, true);
      this.proxyForms.forEach((form) => {
        form.removeEventListener('submit', this.boundFormSubmit, true);
        form.removeEventListener('click', this.boundFormClick, true);
      });
      this.formObserver?.disconnect();
      this.proxyObservers.forEach((observer) => observer.disconnect());
      document.removeEventListener('variant:changed', this.boundVariantChange);
      document.removeEventListener('keydown', this.boundKeydown);
    }

    initElements() {
      this.unitButtons = [...this.querySelectorAll('[data-unit]')];
      this.modeButtons = [...this.querySelectorAll('[data-mode]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.sizeCards = [...this.querySelectorAll('.pms__pill, .pms__size-chip')];
      this.sizeRadios = this.sizeCards
        .map((card) => card.querySelector('input[type="radio"]'))
        .filter(Boolean);
      this.customFields = [...this.querySelectorAll('[data-custom-field]')];
      this.chartCells = [...this.querySelectorAll('[data-inch][data-cm]')];
      this.sizeValues = [...this.querySelectorAll('.pms__pill-text')];
      this.unitLabels = [...this.querySelectorAll('[data-unit-label]')];
      this.unitSuffixes = [...this.querySelectorAll('[data-unit-suffix]')];
      this.sizingTypeField = this.querySelector('[data-sizing-type]');
      this.measurementUnitField = this.querySelector('[data-measurement-unit]');
      this.guideButton = this.querySelector('[data-open-size-guide]');
      this.modal = this.nextElementSibling?.matches('[data-size-guide-modal]')
        ? this.nextElementSibling
        : null;
      this.modalCloseButtons = [...(this.modal?.querySelectorAll('[data-close-modal]') || [])];
    }

    bindEvents() {
      this.unitButtons.forEach((button) => {
        button.addEventListener('click', () => this.setUnit(button.dataset.unit));
      });
      this.modeButtons.forEach((button) => {
        button.addEventListener('click', () => this.setMode(button.dataset.mode));
      });
      this.sizeCards.forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        input?.addEventListener('change', () => this.selectCard(card));
        card.addEventListener('click', () => {
          if (!input || input.disabled || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.customFields.forEach((field) => {
        field.addEventListener('input', () => {
          this.validateField(field);
          this.syncPurchaseControls();
        });
        field.addEventListener('change', () => this.syncPurchaseControls());
        field.addEventListener('blur', () => this.validateField(field));
      });
      this.guideButton?.addEventListener('click', () => this.openModal());
      this.modalCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
      document.addEventListener('variant:changed', this.boundVariantChange);
      document.addEventListener('keydown', this.boundKeydown);
    }

    connectForm(attempt = 0) {
      const formId = this.dataset.formId;
      const form = formId ? document.getElementById(formId) : null;
      if (!form) {
        if (attempt < 20) window.setTimeout(() => this.connectForm(attempt + 1), 50);
        return;
      }
      if (this.form === form) return;

      this.form = form;
      this.variantAvailable = form.dataset.variantAvailable !== 'false';
      form.addEventListener('submit', this.boundFormSubmit, true);
      form.addEventListener('click', this.boundFormClick, true);
      this.formObserver?.disconnect();
      this.formObserver = new MutationObserver(() => this.syncPurchaseControls());
      this.formObserver.observe(form, { childList: true, subtree: true });
      this.connectProxyForms();
      this.syncPurchaseControls();
    }

    connectProxyForms() {
      const productId = String(this.dataset.productId || '');
      if (!productId) return;
      const selector = `form[data-sizing-proxy-product-id="${CSS.escape(productId)}"]`;
      this.proxyForms = [...document.querySelectorAll(selector)].filter((form) => form !== this.form);
      this.proxyForms.forEach((form) => {
        form.addEventListener('submit', this.boundFormSubmit, true);
        form.addEventListener('click', this.boundFormClick, true);
        const observer = new MutationObserver((mutations) => {
          const onlySizingPropertiesChanged = mutations.every((mutation) =>
            [...mutation.addedNodes, ...mutation.removedNodes].every((node) =>
              node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-sizing-proxy-property')
            )
          );
          if (!onlySizingPropertiesChanged) this.syncProxyForm(form, this.isValid());
        });
        observer.observe(form, { childList: true, subtree: true });
        this.proxyObservers.push(observer);
      });
    }

    setUnit(unit) {
      if (unit !== 'inch' && unit !== 'cm') return;
      const previousUnit = this.currentUnit;
      this.currentUnit = unit;

      if (previousUnit !== unit) {
        this.customFields.forEach((field) => {
          if (field.tagName !== 'INPUT') return;
          const value = Number.parseFloat(field.value);
          if (!Number.isFinite(value)) return;
          const converted = unit === 'cm' ? value * 2.54 : value / 2.54;
          field.value = String(Math.round(converted * 10) / 10);
        });
      }

      this.unitButtons.forEach((button) => {
        const active = button.dataset.unit === unit;
        button.classList.toggle('pms__unit-btn--active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.updateDisplay();
      this.syncPurchaseControls();
    }

    updateDisplay() {
      const isInch = this.currentUnit === 'inch';
      const unitText = isInch ? 'in' : 'cm';
      const unitLabel = isInch ? 'inches' : 'centimeters';

      this.sizeValues.forEach((node) => {
        const value = isInch ? node.dataset.unitInch : node.dataset.unitCm;
        if (value) node.textContent = value;
      });
      this.sizeRadios.forEach((input) => {
        if (input.dataset.inch && input.dataset.cm) {
          input.value = `${isInch ? input.dataset.inch : input.dataset.cm} ${unitText}`;
        }
      });
      this.chartCells.forEach((cell) => {
        const value = isInch ? cell.dataset.inch : cell.dataset.cm;
        if (value) cell.textContent = value;
      });
      this.unitLabels.forEach((node) => { node.textContent = unitLabel; });
      this.unitSuffixes.forEach((node) => { node.textContent = unitText; });
      if (this.measurementUnitField) this.measurementUnitField.value = unitLabel;

      this.customFields.forEach((field) => {
        if (field.tagName !== 'INPUT' || field.type !== 'number') return;
        const min = isInch ? field.dataset.minInch : field.dataset.minCm;
        const max = isInch ? field.dataset.maxInch : field.dataset.maxCm;
        if (min) field.min = min;
        if (max) field.max = max;
        this.validateField(field);
      });
    }

    setMode(mode) {
      if (mode !== 'standard' && mode !== 'custom') return;
      this.currentMode = mode;
      const custom = mode === 'custom';

      this.modeButtons.forEach((button) => {
        const active = button.dataset.mode === mode;
        button.classList.toggle('pms__mode-btn--active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.panel === mode;
        panel.classList.toggle('pms__panel--active', active);
      });
      this.sizeRadios.forEach((input) => {
        input.disabled = custom;
        if (custom) input.checked = false;
      });
      this.sizeCards.forEach((card) => {
        if (custom) card.classList.remove('pms__pill--selected', 'pms__size-chip--selected');
      });
      this.customFields.forEach((field) => {
        field.disabled = !custom;
        if (!custom) {
          field.value = '';
          field.classList.remove('is-invalid', 'is-valid');
        }
      });
      if (this.sizingTypeField) this.sizingTypeField.value = custom ? 'Custom' : 'Standard';
      this.clearError();
      this.syncPurchaseControls();
    }

    selectCard(selectedCard) {
      this.sizeCards.forEach((card) => {
        card.classList.remove('pms__pill--selected', 'pms__size-chip--selected');
      });
      selectedCard.classList.add(
        selectedCard.classList.contains('pms__pill') ? 'pms__pill--selected' : 'pms__size-chip--selected'
      );
      this.highlightChartRow(selectedCard);
      this.clearError();
      this.syncPurchaseControls();
    }

    highlightChartRow(card) {
      const selectedSize = card.querySelector('input')?.value;
      this.querySelectorAll('.pms__chart-table tbody tr').forEach((row) => {
        row.classList.toggle('pms__chart-row--selected', row.dataset.size === selectedSize);
      });
    }

    customFieldIsValid(field) {
      if (field.disabled || field.dataset.required !== 'true') return true;
      const value = Number.parseFloat(field.value);
      const min = Number.parseFloat(field.min);
      const max = Number.parseFloat(field.max);
      return Number.isFinite(value) && value >= min && value <= max;
    }

    validateField(field) {
      if (field.disabled || field.dataset.required !== 'true') return true;
      const valid = this.customFieldIsValid(field);
      field.classList.toggle('is-invalid', !valid && field.value !== '');
      field.classList.toggle('is-valid', valid);
      field.setAttribute('aria-invalid', String(!valid));
      return valid;
    }

    isValid() {
      if (this.dataset.required === 'false') return true;
      if (this.currentMode === 'standard') {
        return this.sizeRadios.some((input) => !input.disabled && input.checked);
      }
      const requiredFields = this.customFields.filter(
        (field) => !field.disabled && field.dataset.required === 'true'
      );
      return requiredFields.length > 0 && requiredFields.every((field) => this.customFieldIsValid(field));
    }

    validate(options = {}) {
      if (this.isValid()) {
        this.clearError();
        return true;
      }

      if (this.currentMode === 'standard') {
        this.showError('Please choose a size before adding this item to your bag.');
        if (options.focus) this.sizeRadios[0]?.focus();
      } else {
        const firstInvalid = this.customFields.find(
          (field) => !field.disabled && field.dataset.required === 'true' && !this.validateField(field)
        );
        this.showError('Please enter every required measurement with a valid number.');
        if (options.focus) firstInvalid?.focus();
      }
      return false;
    }

    showError(message) {
      let error = this.querySelector('.pms__error');
      if (!error) {
        error = document.createElement('div');
        error.className = 'pms__error';
        error.setAttribute('role', 'alert');
        this.append(error);
      }
      error.textContent = message;
    }

    clearError() {
      this.querySelector('.pms__error')?.remove();
    }

    syncPurchaseControls() {
      if (!this.form) return;
      const valid = this.isValid();
      this.syncFormControls(this.form, valid, false);
      this.proxyForms.forEach((form) => this.syncProxyForm(form, valid));
    }

    syncFormControls(form, valid, proxy) {
      const quantityBlocked = form.dataset.quantityBlocked === 'true';
      const canPurchase = valid && this.variantAvailable && !quantityBlocked;
      form.classList.toggle('ps-form-sizing-locked', !valid);
      form.dataset.sizingBlocked = String(!valid);

      const addButton = form.querySelector('[name="add"]');
      if (addButton) {
        if (!canPurchase) {
          addButton.disabled = true;
          if (!valid) addButton.dataset.sizingLocked = 'true';
          if (!this.variantAvailable) addButton.dataset.variantLocked = 'true';
          addButton.setAttribute('aria-disabled', 'true');
        } else if (
          addButton.dataset.sizingLocked === 'true'
          || addButton.dataset.variantLocked === 'true'
          || addButton.dataset.quantityRuleLocked === 'true'
        ) {
          const quantityLocked = addButton.dataset.quantityRuleLocked === 'true';
          addButton.disabled = false;
          delete addButton.dataset.sizingLocked;
          delete addButton.dataset.variantLocked;
          if (quantityLocked) {
            addButton.disabled = true;
            addButton.setAttribute('aria-disabled', 'true');
          } else {
            addButton.removeAttribute('aria-disabled');
          }
        }
      }

      const checkout = form.querySelector('.m-product-dynamic-checkout, .shopify-payment-button');
      checkout?.classList.toggle('ps-checkout-locked', !canPurchase);
      checkout?.setAttribute('aria-disabled', String(!canPurchase));
      if (checkout) {
        checkout.inert = !canPurchase;
        checkout.style.pointerEvents = canPurchase ? '' : 'none';
        checkout.toggleAttribute('data-sizing-checkout-locked', !valid || !this.variantAvailable);
      }
      form.querySelectorAll('.shopify-payment-button button').forEach((button) => {
        button.disabled = !canPurchase;
      });

      if (!proxy) return;
      form.querySelectorAll('[data-sizing-proxy-property]').forEach((field) => field.remove());
      if (!valid) return;
      this.querySelectorAll('[name^="properties["]').forEach((source) => {
        if (source.disabled || !source.value) return;
        if ((source.type === 'radio' || source.type === 'checkbox') && !source.checked) return;
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = source.name;
        field.value = source.value;
        field.dataset.sizingProxyProperty = 'true';
        form.append(field);
      });
    }

    syncProxyForm(form, valid) {
      this.syncFormControls(form, valid, true);
    }

    onFormSubmit(event) {
      if (this.validate({ focus: true })) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onFormClick(event) {
      const checkoutTrigger = event.target.closest(
        '.shopify-payment-button, .shopify-payment-button__button, [data-testid*="Checkout"]'
      );
      if (!checkoutTrigger || this.validate({ focus: true })) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onVariantChange(event) {
      const sectionId = String(event.detail?.sectionId || '');
      if (!sectionId || sectionId !== String(this.dataset.sectionId)) return;
      this.variantAvailable = Boolean(event.detail?.variant?.available);
      if (this.form) this.form.dataset.variantAvailable = String(this.variantAvailable);
      this.syncPurchaseControls();
    }

    openModal() {
      if (!this.modal) return;
      this.lastFocusedElement = this.guideButton || document.activeElement;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => this.modal.querySelector('.pms__modal-close')?.focus(), 100);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.lastFocusedElement?.focus?.();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.modal?.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    }
  }

  customElements.define('product-measurement-sizing', ProductMeasurementSizing);
}
